import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formattedDate, cleanString } from "../utils";
import AmendIcon from "./../assets/amendment.svg";
import { useGetReportInfoList } from "../api/ReportsApi";
import Table from "./Table";
import {
  ReportSummaryApi,
  ReportSummaryItemApi,
  WORKFLOW_REPORT_STATUS_ENUM,
} from "../api/Reports";
import Badge from "../shared-components/Badge";
import Pagination from "../shared-components/Pagination";
import PDFDownloadWrapper from "./PDFDownloadWrapper";
import ArrowDownTrayIcon from "../shared-components/icons/ArrowDownTrayIcon";
import { WORKFLOW_STATUS_ENUM } from "../api/Workflows";
import { useAppContext } from "../AppProviders";
import { CacheKeys } from "../api/Cache";
import { LoadedSince } from "../shared-components/LoadedSince";
import {
  ArrowPathIcon as RefreshIcon,
  ArrowPathRoundedSquareIcon,
} from "@heroicons/react/24/outline";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { genericErrorLoadingMessage } from "./utils";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useFamaFlags } from "../api/FamaFlags";
import ReportDeleteButton from "./ReportDeleteButton";
import ReactTooltip from "react-tooltip";
import StatusAccent from "../shared-components/StatusAccent";
import { getSummaryPath } from "./useNavigateToSummaryWithState";
import { cacheUpdateEvents } from "../services/websocket/cacheUpdateEvents";

const ReportsCompleted = () => {
  const location = useLocation();
  const { canViewAllReportsWithinCompany } = usePermissionValidator();
  const {
    enableProductReportsForClientTab,
    v3UiCompletedListWith360,
    v3UiReactsToWebsocketWorkflowEvents: workflowEventsEnabled,
  } = useFamaFlags();
  const {
    isLoading: loadingReports,
    data: reportsData,
    mutate: getReportsFetchList,
    error: reportsError,
  } = useGetReportInfoList(
    canViewAllReportsWithinCompany,
    enableProductReportsForClientTab
  );

  const { refreshCache } = useAppContext();

  const pageSize = 15;
  const search = location.search;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");

  const latestRef = useRef({
    filter,
    page,
    reportsData,
    getReportsFetchList,
  });
  latestRef.current = {
    filter,
    page,
    reportsData,
    getReportsFetchList,
  };

  useEffect(() => {
    if (!workflowEventsEnabled) return;
    const unsubscribe = cacheUpdateEvents.subscribe((affectedReportUuids) => {
      const current = latestRef.current.reportsData?.report_summaries ?? [];
      const hasAffected = affectedReportUuids.some((uuid) =>
        current.some(
          (r) => r.report_uuid === uuid || r.workflow_run_uuid === uuid
        )
      );
      if (hasAffected) {
        const {
          filter: f,
          page: p,
          getReportsFetchList: fetchList,
        } = latestRef.current;
        fetchList({
          person_name: f,
          page_size: pageSize,
          page: p - 1,
          workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.COMPLETED],
          descending: true,
        });
      }
    });
    return unsubscribe;
  }, [workflowEventsEnabled, pageSize]);

  const tableHeader = [
    "Name / Company",
    "Date Completed",
    "Report ID",
    "Met SLA?",
    "Check Type / Behavior Kit",
    "Total Flags",
    "Date Added",
    "Client Reference ID",
    "Remove",
  ];

  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  useEffect(() => {
    getReportsFetchList({
      person_name: filter,
      page_size: pageSize,
      page: page - 1,
      workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.COMPLETED],
      descending: true,
    });
  }, [getReportsFetchList, filter, page]);

  function clearCache() {
    refreshCache(CacheKeys.reports);
    getReportsFetchList({
      person_name: filter,
      page_size: pageSize,
      page: page - 1,
      workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.COMPLETED],
      descending: true,
    });
  }

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  const convertReportsCompletedToRows = (
    reportsData: ReportSummaryApi | undefined
  ): ReportRowItem<ReportSummaryItemApi>[] => {
    if (!reportsData) return [];
    const reportRows: ReportRowItem<ReportSummaryItemApi>[] =
      reportsData.report_summaries.map((report: ReportSummaryItemApi) => {
        const data: ReportEntry[] = [
          {
            text: report.full_name,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <div className="flex items-center">
                <div>
                  <Link
                    to={getSummaryPath(report, v3UiCompletedListWith360)}
                    state={{
                      originUrl: `${location.pathname}${location.search}`,
                      report,
                    }}
                    className="underline cursor-pointer font-semibold"
                  >
                    {report.full_name}
                  </Link>
                  <small className="block font-normal">
                    {report.company_name}
                  </small>
                </div>
                {(report.step_payload?.rerun_from_parent_uuid ||
                  report.has_rerun) && (
                  <div>
                    <RefreshIcon className="w-5 ml-2 text-blue-600" />
                  </div>
                )}
                {report.was_amend && (
                  <img
                    className={"w-5 ml-2 text-blue-600"}
                    // @ts-ignore
                    src={AmendIcon}
                    alt={"amendment"}
                  />
                )}
              </div>
            ),
          },
          {
            text:
              report.workflow_status === WORKFLOW_STATUS_ENUM.CANCELLED
                ? "CANCELLED"
                : formattedDate(report.finish_at),
            style: ReportEntryStyle.TEXT,
          },
          {
            text: report.report_uuid,
            style: ReportEntryStyle.COMPONENT,
            component:
              report.workflow_status === WORKFLOW_STATUS_ENUM.CANCELLED ? (
                <span className="flex w-20 text-xs text-gray-800 truncate">
                  {report.report_uuid}
                </span>
              ) : (
                <PDFDownloadWrapper report_uuid={report.report_uuid}>
                  <div>
                    <div className="flex relative w-20">
                      <span className="text-xs text-gray-800 underline cursor-pointer truncate">
                        <StatusAccent report={report} />
                        {report.report_uuid}
                      </span>
                      <div>
                        <ArrowDownTrayIcon />
                      </div>
                    </div>
                  </div>
                </PDFDownloadWrapper>
              ),
          },
          {
            text: "",
            style: ReportEntryStyle.COMPONENT,
            component:
              report.sla_met === undefined || report.sla_met === null ? (
                <Badge color="gray">N/A</Badge>
              ) : report.sla_met ? (
                <Badge color="green">Yes</Badge>
              ) : (
                <Badge color="red">No</Badge>
              ),
          },
          {
            component: (
              <div className="flex flex-col">
                <span className="border-b-2">{report.blueprint_name}</span>
                <small className="block">
                  {report.behavior_kit_name ?? "General"}
                </small>
              </div>
            ),
            text:
              report.blueprint_name +
              " " +
              (report.behavior_kit_name ?? "General"),
            style: ReportEntryStyle.COMPONENT,
          },
          {
            text: `${report.total_ratings}`,
            style: ReportEntryStyle.TOOLTIP,
          },
          {
            text: formattedDate(report.report_date_added),
            style: ReportEntryStyle.COMPONENT,
            component: report.recur ? (
              <>
                <span>{formattedDate(report.report_date_added)}</span>
                <span data-tip="Employee Screening" className="cursor-pointer">
                  <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
                </span>
                <ReactTooltip effect="solid" />
              </>
            ) : (
              <span>{formattedDate(report.report_date_added)}</span>
            ),
          },
          {
            text: `${report.client_report_id}`,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: "Delete",
            style: ReportEntryStyle.COMPONENT,
            component: (
              <ReportDeleteButton
                reportUUID={report.report_uuid}
                onDeletedSuccess={() =>
                  getReportsFetchList({
                    person_name: filter,
                    page_size: pageSize,
                    page: page - 1,
                    workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.COMPLETED],
                    descending: true,
                  })
                }
              />
            ),
          },
        ];

        return {
          report: report,
          report_uuid: report.report_uuid,
          data: data,
          isLocked: report.is_locked,
        };
      });
    return reportRows;
  };

  return (
    <>
      <LoadedSince reportData={reportsData} onClear={clearCache} />
      <Table
        isLoading={loadingReports}
        header={tableHeader}
        data={convertReportsCompletedToRows(reportsData)}
      />
      {!loadingReports && reportsData && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={reportsData.total_rows}
          search={filter}
        />
      )}
    </>
  );
};

export default ReportsCompleted;
